<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="680px"
    content-class="glass-dialog"
  >
    <v-card class="modern-card d-flex flex-column" style="max-height: 80vh">
      <!-- Header with animated close button -->
      <div class="dialog-header">
        <h2 class="dialog-title">Create New Ad</h2>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          x-large
          depressed
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Scrollable content area -->
      <div style="flex-grow: 1; overflow: auto">
        <v-card-text class="dialog-content">
          <div class="dialog-subtitle">Enter details for the new advertisement</div>

          <!-- Ad Type Selection -->
          <div class="form-section">
            <div class="section-title">Select Ad Type</div>
            <v-select
              :items="typeOptions"
              v-model="profile.type"
              outlined
              rounded
              dense
              color="accent"
              hide-details="auto"
              @change="onChange"
              class="modern-input"
            ></v-select>
          </div>

          <!-- Video Upload Section -->
          <div v-if="profile.type == 'video'" class="form-section">
            <v-btn 
              @click="onSelectVideo" 
              depressed
              rounded
              color="primary"
              class="mb-4 upload-btn"
            >
              <v-icon left>mdi-video</v-icon>
              Select Video
            </v-btn>
            <vue-plyr
              ref="plyr"
              :options="{ autoplay: false, muted: true }"
              v-if="selectedVideo"
              class="video-preview"
            >
              <video controls crossorigin playsinline>
                <source :src="selectedVideo" type="video/mp4" />
              </video>
            </vue-plyr>
          </div>

          <!-- Image Upload Section -->
          <template v-if="profile.type == 'image'">
            <div class="form-section">
              <div class="section-title">Select Ad Image</div>
              <div class="d-flex justify-center">
                <editable-ads-image
                  :image="profile.file"
                  :onImageSelected="onImageSelected"
                  :ratio="1 / 1"
                  class="image-uploader"
                />
              </div>
            </div>

            <div class="form-section">
              <div class="section-title">Select Banner Image</div>
              <div class="d-flex justify-center">
                <editable-ads-image
                  :image="profile.banner"
                  :onImageSelected="onBannerImageSelected"
                  :ratio="728 / 90"
                  class="image-uploader"
                />
              </div>
            </div>
          </template>

          <!-- Business Selection -->
          <div class="form-section">
            <v-select
              label="Business"
              outlined
              rounded
              dense
              color="accent"
              v-model="profile.business"
              :rules="[rules.required]"
              hide-details="auto"
              :items="business"
              item-text="name"
              item-value="_id"
              class="modern-input"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="accent">mdi-domain</v-icon>
              </template>
            </v-select>
          </div>

          <!-- URL Input -->
          <div class="form-section">
            <v-text-field
              label="Destination URL"
              outlined
              rounded
              dense
              color="accent"
              v-model="profile.url"
              :rules="[rules.required]"
              hide-details="auto"
              class="modern-input"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="accent">mdi-link</v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Ad Text -->
          <div class="form-section">
            <v-textarea
              label="Ad Text (Optional)"
              outlined
              rounded
              dense
              color="accent"
              v-model="profile.text"
              rows="3"
              auto-grow
              hide-details="auto"
              class="modern-textarea"
              counter="250"
            >
              <template v-slot:prepend-inner>
                <v-icon small color="accent">mdi-text</v-icon>
              </template>
            </v-textarea>
          </div>

          <!-- Error Message -->
          <transition name="slide-fade">
            <v-alert
              v-if="message"
              type="error"
              dense
              outlined
              class="error-alert mt-4"
            >
              {{ message }}
            </v-alert>
          </transition>
        </v-card-text>
      </div>

      <!-- Progress Bar -->
      <v-card-text v-if="uploadProgress && loading" class="px-6">
        <v-progress-linear 
          :value="uploadProgress" 
          color="primary"
          height="8"
          rounded
          class="upload-progress"
        />
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="dialog-actions">
        <v-btn
          text
          large
          @click="onClose"
          class="cancel-btn"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          large
          @click="onSave"
          :loading="loading"
          depressed
          class="submit-btn"
          dark
        >
          <v-icon left>mdi-plus</v-icon>
          Create Ad
        </v-btn>
      </v-card-actions>

      <!-- Hidden file input -->
      <input
        ref="uploader"
        class="d-none"
        type="file"
        :value="file"
        accept="video/*"
        @change="onFileChanged"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EditableAdsImage from "./EditableAdsImage.vue";

export default {
  components: { EditableAdsImage },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      name: "",
      message: null,
      loading: false,
      rules: {
        required: value => !!value || "Required."
      },
      selectedImage: null,
      selectedBannerImage: null,
      selectedVideo: null,
      selectedFile: null,
      profile: { type: "image" },
      typeOptions: ["image", "video"],
      errorMessage: null,
      snackbar: false,
      file: null
    };
  },
  methods: {
    ...mapActions({
      addAds: "ads/addAds",
      fetchAllBusiness: "business/fetchAllBusiness"
    }),
    onChange() {
      this.message = null;
    },
    scrollToError() {
      this.$nextTick(() => {
        if (this.$refs.errorMessage) {
          this.$refs.errorMessage.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      });
    },
    onSave() {
      if (
        !this.profile.file &&
        !this.selectedImage &&
        this.profile.type == "image"
      ) {
        this.message = "Please select the ad image.";
        this.scrollToError();
        return;
      }
      if (
        !this.profile.banner &&
        !this.selectedBannerImage &&
        this.profile.type == "image"
      ) {
        this.message = "Please select the banner image.";
        this.scrollToError();
        return;
      }
      if (!this.selectedFile && this.profile.type == "video") {
        this.message = "Please select the video.";
        this.scrollToError();
        return;
      }

      if (!this.profile.url) {
        this.message = "Please add the destination URL.";
        this.scrollToError();
        return;
      }
      if (!this.profile.business) {
        this.message = "Please select a business.";
        this.scrollToError();
        return;
      }
      
      this.message = null;
      var formData = new FormData();
      if (this.selectedImage) {
        formData.append("file", this.selectedImage);
      }
      if (this.selectedBannerImage) {
        formData.append("banner", this.selectedBannerImage);
      }
      if (this.profile.text) {
        formData.append("text", this.profile.text);
      }
      if (this.selectedFile) {
        formData.append("file", this.selectedFile);
      }
      formData.append("type", this.profile.type);
      formData.append("business", this.profile.business);
      formData.append("url", this.profile.url);
      
      var bs;
      this.business.map(b => {
        if (b._id == this.profile.business) {
          bs = b.name;
        }
      });
      formData.append("business_name", bs);
      
      this.loading = true;
      this.addAds(formData)
        .then(() => {
          this.loading = false;
          this.onClose();
        })
        .catch(error => {
          this.loading = false;
          if (error.response) {
            this.message = error.response.data.message;
          } else {
            this.message = error;
          }
          this.scrollToError();
        });
    },
    onImageSelected(image) {
      this.selectedImage = image;
      this.message = null;
    },
    onBannerImageSelected(image) {
      this.selectedBannerImage = image;
      this.message = null;
    },
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },
    onSelectVideo() {
      setTimeout(() => {
        window.addEventListener("focus", () => {}, { once: true });
        this.$refs.uploader.click();
      }, 300);
    },
    onFileChanged(e) {
      if (e.target.files.length != 0) {
        this.selectedFile = e.target.files[0];
        if (this.type == "image") {
          this.selectedImage = URL.createObjectURL(this.selectedFile);
        } else {
          this.selectedVideo = URL.createObjectURL(this.selectedFile);
        }
      }
    },
    onRemoveAttachment() {
      if (this.profile.type == "video") {
        setTimeout(() => {
          this.selectedVideo = null;
          this.selectedBannerImage = null;
          this.selectedImage = null;
        }, 200);
      } else {
        this.selectedVideo = null;
        this.selectedBannerImage = null;
        this.selectedImage = null;
      }
    }
  },
  computed: {
    ...mapGetters({
      business: "business/getAllBusiness",
      uploadProgress: "ads/getUploadProgress"
    })
  },
  mounted() {
    this.loading = true;
    this.fetchAllBusiness()
      .then(() => {
        this.loading = false;
      })
      .catch(error => {
        this.loading = false;
        if (error.response == undefined) {
          console.log(error);
        } else {
          this.showErrorMessage(error.response.data.message);
        }
      });
  }
};
</script>

<style>
/* Glass morphism effect */
.glass-dialog {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.modern-card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18) !important;
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  background: linear-gradient(135deg,#084468,#3b7ca1);
  color: white;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.5px;
  margin: 0;
}

.dialog-subtitle {
  font-size: 1rem;
  color: #666;
  margin-bottom: 24px;
}

.close-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  border-radius: 50%;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  padding: 24px 32px;
}

.form-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
}

.modern-input .v-input__slot,
.modern-textarea .v-input__slot {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  background: rgba(245, 245, 245, 0.7) !important;
}

.modern-input .v-input__slot:hover,
.modern-textarea .v-input__slot:hover {
  background: rgba(245, 245, 245, 0.9) !important;
}

.modern-input .v-input__slot fieldset,
.modern-textarea .v-input__slot fieldset {
  border-width: 1px !important;
  border-color: rgba(0, 0, 0, 0.1) !important;
}

.modern-input .v-label,
.modern-textarea .v-label {
  color: #666 !important;
  font-size: 0.9rem;
}

.image-uploader {
  border: 2px dashed rgba(98, 0, 238, 0.3);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.image-uploader:hover {
  border-color: rgba(98, 0, 238, 0.6);
}

.upload-btn {
  letter-spacing: 0.5px;
  text-transform: none;
  font-weight: 500;
}

.video-preview {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.error-alert {
  margin-top: 16px;
  border-radius: 8px !important;
  border-left: 4px solid #ff5252 !important;
}

.upload-progress {
  margin-top: 8px;
}

.dialog-actions {
  padding: 16px 32px;
  background: rgba(245, 245, 245, 0.7);
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.cancel-btn {
  color: #666 !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
}

.cancel-btn:hover {
  color: #333 !important;
}

.submit-btn {
  background: linear-gradient(135deg,#084468,#3b7ca1) !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(98, 0, 238, 0.2);
  transition: all 0.3s ease;
  padding: 0 24px !important;
  letter-spacing: 0.5px;
  text-transform: none;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(98, 0, 238, 0.3);
}

.submit-btn:active {
  transform: translateY(0);
}

/* Animations */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>